import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { LuTrash2 } from "react-icons/lu";
import { api, ApiError , errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useActiveFlat } from "../lib/active-flat";
import { useCurrency } from "../lib/currency";
import type { Debt, Expense, SettlementEntry } from "../lib/types";
import { venmoLink, ZELLE_URL } from "../lib/payLinks";
import { Avatar } from "../components/Avatar";
import { Modal } from "../components/Modal";
import {
  Button,
  Card,
  CenteredSpinner,
  EmptyState,
  ErrorBanner,
  IconButton,
  Input,
  Label,
  SectionHeader,
} from "../components/ui";
import { formatDate, formatMoney, poundsToPence } from "../lib/format";

type Data = {
  expenses: Expense[];
  summary: { settlement: SettlementEntry[]; debts: Debt[] };
};

export function ExpensesPage() {
  const { user } = useAuth();
  const { flat } = useActiveFlat();
  const { symbol } = useCurrency();
  const flatId = flat!.id;

  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await api.get<Data>(`/expense/by-flat/${flatId}`);
      setData(result);
    } catch (err) {
      setError(errMsg(err, "Couldn't load expenses."));
    }
  }, [flatId]);

  useEffect(() => {
    load();
  }, [load]);

  const monthlyByPayer = useMemo(() => {
    if (!data) return [];
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    const totals = new Map<string, { name: string; colour: string; value: number }>();
    for (const exp of data.expenses) {
      const d = new Date(exp.createdAt);
      if (d.getMonth() !== month || d.getFullYear() !== year) continue;
      const existing = totals.get(exp.paidById) ?? {
        name: exp.paidBy.name ?? "Unknown",
        colour: exp.paidBy.colour,
        value: 0,
      };
      existing.value += exp.amount;
      totals.set(exp.paidById, existing);
    }
    return Array.from(totals.values());
  }, [data]);

  const settleSplit = async (expenseId: string, userId: string) => {
    try {
      await api.patch(`/expense/${expenseId}/split/${userId}/settle`);
      await load();
    } catch (err) {
      setError(errMsg(err, "Couldn't settle the split."));
    }
  };

  const settleUp = async (toUserId: string) => {
    try {
      await api.post(`/expense/by-flat/${flatId}/settle-up`, { toUserId });
      await load();
    } catch (err) {
      setError(errMsg(err, "Couldn't settle up."));
    }
  };

  const deleteExpense = async (id: string) => {
    if (!window.confirm("Delete this expense? This cannot be undone.")) return;
    try {
      await api.delete(`/expense/${id}`);
      await load();
    } catch (err) {
      setError(errMsg(err, "Couldn't delete."));
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Expenses</h1>
          <p className="text-sm text-slate-500">Track shared spending and settle up.</p>
        </div>
        <Button onClick={() => setShowAdd(true)}>Add expense</Button>
      </div>

      <ErrorBanner message={error} />

      {data === null ? (
        <CenteredSpinner />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <SectionHeader title="This month" subtitle="Who's paid for what." />
              {monthlyByPayer.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500">No expenses logged yet this month.</p>
              ) : (
                <div className="flex flex-col items-center gap-4 sm:flex-row">
                  <div className="h-44 w-full max-w-[180px]">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={monthlyByPayer}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={45}
                          outerRadius={75}
                          paddingAngle={2}
                          stroke="white"
                          strokeWidth={2}
                        >
                          {monthlyByPayer.map((entry, idx) => (
                            <Cell key={idx} fill={entry.colour} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v: number) => formatMoney(v, symbol)}
                          contentStyle={{ borderRadius: 8, fontSize: 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="flex-1 space-y-2 text-sm">
                    {monthlyByPayer.map((p) => (
                      <li key={p.name} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block h-3 w-3 rounded-full"
                            style={{ background: p.colour }}
                          />
                          <span className="text-slate-700">{p.name}</span>
                        </div>
                        <span className="font-semibold text-slate-900">{formatMoney(p.value, symbol)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>

            <Card>
              <SectionHeader title="Settlement" subtitle="The simplest path to even." />
              <SettlementList
                entries={data.summary.settlement}
                currentUserId={user!.id}
              />
            </Card>
          </div>

          <SettleUpSection
            debts={data.summary.debts.filter((d) => d.fromUserId === user!.id)}
            onSettle={settleUp}
          />

          <section>
            <SectionHeader title="All expenses" subtitle="Newest first." />
            {data.expenses.length === 0 ? (
              <EmptyState
                title="No expenses yet"
                description="Add your first shared expense to start tracking."
                action={<Button onClick={() => setShowAdd(true)}>Add expense</Button>}
              />
            ) : (
              <motion.div
                className="space-y-3"
                initial="hidden"
                animate="show"
                variants={{ show: { transition: { staggerChildren: 0.06 } } }}
              >
                <AnimatePresence initial={false}>
                  {data.expenses.map((exp) => (
                    <motion.div
                      key={exp.id}
                      layout
                      variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0, transition: { duration: 0.2 } } }}
                      exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.15 } }}
                    >
                      <ExpenseRow
                        expense={exp}
                        currentUserId={user!.id}
                        onSettle={settleSplit}
                        onDelete={deleteExpense}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </section>
        </>
      )}

      <AddExpenseModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSaved={() => {
          setShowAdd(false);
          load();
        }}
      />
    </div>
  );
}

function SettlementList({
  entries,
  currentUserId,
}: {
  entries: SettlementEntry[];
  currentUserId: string;
}) {
  const { symbol } = useCurrency();
  if (entries.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-500">All settled up.</p>;
  }
  return (
    <ul className="space-y-2">
      {entries.map((entry) => {
        if (entry.net === 0) return null;
        const isMe = entry.userId === currentUserId;
        const owed = entry.net > 0;
        return (
          <li
            key={entry.userId}
            className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2"
          >
            <div className="flex items-center gap-2">
              <Avatar name={entry.user.name} colour={entry.user.colour} size="sm" />
              <div>
                <div className="text-sm font-medium text-slate-900">
                  {isMe ? "You" : entry.user.name}
                </div>
                <div className="text-xs text-slate-500">
                  {owed
                    ? isMe
                      ? "is owed"
                      : "is owed by the flat"
                    : isMe
                      ? "owe the flat"
                      : "owes the flat"}
                </div>
              </div>
            </div>
            <span
              className={`text-sm font-semibold ${
                owed ? "text-emerald-700" : "text-rose-700"
              }`}
            >
              {formatMoney(Math.abs(entry.net), symbol)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function SettleUpSection({
  debts,
  onSettle,
}: {
  debts: Debt[];
  onSettle: (toUserId: string) => Promise<void>;
}) {
  const { symbol } = useCurrency();
  // A debt whose payment we've handed off and are waiting to confirm on return.
  const [armed, setArmed] = useState<Debt | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [copiedHandle, setCopiedHandle] = useState<string | null>(null);

  // When the tab regains focus after the Venmo/Zelle handoff, ask whether the
  // payment actually went through — an abandoned payment must not silently
  // settle the debt (there's no un-settle).
  useEffect(() => {
    if (!armed) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") setConfirming(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [armed]);

  if (debts.length === 0) return null;

  const payVenmo = (debt: Debt) => {
    if (!debt.to.venmoHandle) return;
    setArmed(debt);
    window.open(venmoLink(debt.to.venmoHandle, debt.amount), "_blank", "noopener");
  };

  const payZelle = async (debt: Debt) => {
    if (!debt.to.zelleHandle) return;
    try {
      await navigator.clipboard.writeText(debt.to.zelleHandle);
      setCopiedHandle(debt.toUserId);
      setTimeout(() => setCopiedHandle((c) => (c === debt.toUserId ? null : c)), 2000);
    } catch {
      /* clipboard may be unavailable; the handle is shown on the card anyway */
    }
    setArmed(debt);
    window.open(ZELLE_URL, "_blank", "noopener");
  };

  const confirmPaid = async () => {
    if (!armed) return;
    const toUserId = armed.toUserId;
    setConfirming(false);
    setArmed(null);
    await onSettle(toUserId);
  };

  const dismiss = () => {
    setConfirming(false);
    setArmed(null);
  };

  return (
    <Card>
      <SectionHeader
        title="Settle up"
        subtitle="Pay someone back directly. Handles are visible to everyone in the flat."
      />
      <ul className="space-y-2">
        {debts.map((debt) => (
          <li
            key={debt.toUserId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2.5"
          >
            <div className="flex items-center gap-2">
              <Avatar name={debt.to.name} colour={debt.to.colour} size="sm" />
              <div>
                <div className="text-sm font-medium text-slate-900">
                  You owe {debt.to.name ?? "—"}
                </div>
                <div className="text-xs font-semibold text-rose-700">
                  {formatMoney(debt.amount, symbol)}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {debt.to.venmoHandle && (
                <Button
                  variant="secondary"
                  className="text-xs"
                  onClick={() => payVenmo(debt)}
                >
                  Pay with Venmo
                </Button>
              )}
              {debt.to.zelleHandle && (
                <Button
                  variant="secondary"
                  className="text-xs"
                  onClick={() => payZelle(debt)}
                >
                  {copiedHandle === debt.toUserId ? "Handle copied!" : "Zelle (copy handle)"}
                </Button>
              )}
              <Button
                variant="ghost"
                className="text-xs"
                onClick={() => onSettle(debt.toUserId)}
              >
                Mark paid
              </Button>
            </div>
          </li>
        ))}
      </ul>
      {(debts.some((d) => d.to.venmoHandle || d.to.zelleHandle)) && (
        <p className="mt-3 text-xs text-slate-400">
          Venmo and Zelle are US-only. Zelle can't be prefilled — we copy the handle and open
          zellepay.com so you can finish in your bank's app.
        </p>
      )}

      <Modal
        open={confirming && armed !== null}
        title="Did the payment go through?"
        onClose={dismiss}
      >
        {armed && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Mark your {formatMoney(armed.amount, symbol)} debt to{" "}
              <span className="font-medium text-slate-900">{armed.to.name ?? "them"}</span> as
              settled? Only do this if the payment actually completed — it can't be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={dismiss}>
                Not yet
              </Button>
              <Button onClick={confirmPaid}>Yes, mark settled</Button>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
}

function ExpenseRow({
  expense,
  currentUserId,
  onSettle,
  onDelete,
}: {
  expense: Expense;
  currentUserId: string;
  onSettle: (expenseId: string, userId: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const { symbol } = useCurrency();
  const iPaid = expense.paidById === currentUserId;
  const mySplit = expense.splits.find((s) => s.userId === currentUserId);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Avatar
              name={expense.paidBy.name}
              colour={expense.paidBy.colour}
              size="sm"
              title={expense.paidBy.name ?? undefined}
            />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-slate-900">
                {expense.description}
              </div>
              <div className="text-xs text-slate-500">
                {iPaid ? "You" : expense.paidBy.name} paid · {formatDate(expense.createdAt)}
              </div>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-base font-semibold text-slate-900">
            {formatMoney(expense.amount, symbol)}
          </div>
          {iPaid && (
            <IconButton icon={<LuTrash2 className="h-4 w-4" />} label="Delete expense" onClick={() => onDelete(expense.id)} variant="danger" />
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500">Split with:</span>
          {expense.splits.map((s) => (
            <div
              key={s.id}
              className={`flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-xs ${
                s.settled ? "opacity-50 line-through" : ""
              }`}
              title={`${s.user.name}: ${formatMoney(s.amount, symbol)}${s.settled ? " (settled)" : ""}`}
            >
              <Avatar name={s.user.name} colour={s.user.colour} size="xs" />
              <span className="text-slate-700">
                {s.user.name} · {formatMoney(s.amount, symbol)}
              </span>
            </div>
          ))}
        </div>
        {mySplit && !mySplit.settled && !iPaid && (
          <Button
            variant="secondary"
            onClick={() => onSettle(expense.id, currentUserId)}
            className="text-xs"
          >
            Mark as paid back
          </Button>
        )}
        {iPaid &&
          expense.splits.some((s) => !s.settled && s.userId !== currentUserId) && (
            <details className="text-xs">
              <summary className="cursor-pointer text-brand-600 hover:text-brand-700">
                Settle splits
              </summary>
              <div className="mt-2 space-y-1">
                {expense.splits
                  .filter((s) => !s.settled && s.userId !== currentUserId)
                  .map((s) => (
                    <button
                      key={s.id}
                      onClick={() => onSettle(expense.id, s.userId)}
                      className="block w-full rounded px-2 py-1 text-left text-slate-700 hover:bg-slate-100"
                    >
                      Mark {s.user.name}'s share settled
                    </button>
                  ))}
              </div>
            </details>
          )}
      </div>
    </div>
  );
}

function AddExpenseModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const { flat } = useActiveFlat();
  const { symbol } = useCurrency();
  const members = flat!.members;

  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [splitWith, setSplitWith] = useState<Set<string>>(
    () => new Set(members.map((m) => m.userId)),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAmount("");
      setDescription("");
      setSplitWith(new Set(members.map((m) => m.userId)));
      setError(null);
    }
  }, [open, members]);

  const toggle = (id: string) => {
    setSplitWith((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const pence = poundsToPence(amount);
    if (pence === null || pence <= 0) return setError("Enter an amount greater than 0.");
    if (!description.trim()) return setError("Add a short description.");
    if (splitWith.size === 0) return setError("Pick at least one person to split with.");

    setSubmitting(true);
    try {
      await api.post(`/expense/by-flat/${flat!.id}`, {
        amount: pence,
        description: description.trim(),
        splitWith: Array.from(splitWith),
      });
      onSaved();
    } catch (err) {
      setError(errMsg(err, "Couldn't save the expense."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title="Add expense" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-1">
            <Label htmlFor="amt">Amount ({symbol})</Label>
            <Input
              id="amt"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          </div>
          <div className="col-span-2">
            <Label htmlFor="desc">Description</Label>
            <Input
              id="desc"
              placeholder="Groceries, electricity, etc."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        <div>
          <Label>Split with</Label>
          <div className="flex flex-wrap gap-2">
            {members.map((m) => {
              const selected = splitWith.has(m.userId);
              const isMe = m.userId === user!.id;
              return (
                <button
                  key={m.userId}
                  type="button"
                  onClick={() => toggle(m.userId)}
                  className={`flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs transition ${
                    selected
                      ? "border-brand-300 bg-brand-50 text-brand-800"
                      : "border-slate-200 text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  <Avatar name={m.user.name} colour={m.user.colour} size="xs" />
                  <span>{isMe ? "Me" : m.user.name}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            The expense is split equally between the selected people.
          </p>
        </div>

        <ErrorBanner message={error} />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            Save expense
          </Button>
        </div>
      </form>
    </Modal>
  );
}
